import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { IconButton } from '@ui/button';
import { ActivityCardGroup, ResponsiveImage } from '@web/molecules';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { Question } from '../Question';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { StyledFormWithImage } from '../StyledForm';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import {
  FieldWrapper,
  ImageContainer,
  ButtonContainer,
  MobileImageContainer,
  DesktopImageContainer,
} from './styles.js';

export const ActivityForm = ({
  activityQuestionPrompt,
  defaultValues,
  goBack,
  onSubmit,
  index,
  levels,
  subheadline,
  mobileImage,
  desktopImage,
  flowType,
}) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted },
    reset,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });
  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCartContext();

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('activity_step', cart?.customer || customer),
    );
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(
        index,
        'dog_activity',
        cart?.customer || customer,
        data,
        flowType,
      ),
    );
    const result = await onSubmit(data);
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledFormWithImage>
      <ImageContainer>
        <ButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isSubmitting || isSubmitted}
            type="button"
            variant="primary"
          />
        </ButtonContainer>
        <MobileImageContainer>
          <ResponsiveImage priority={true} {...mobileImage} width={400} />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <FormWrapper $minHeight={'280'}>
        <FormFieldsContainer>
          <Question
            gap={0}
            prompt={activityQuestionPrompt}
            subheadline={subheadline}
          >
            <FieldWrapper>
              <Controller
                control={control}
                name="activity"
                render={({ field: { ref, ...field } }) => (
                  <>
                    <ActivityCardGroup
                      {...field}
                      choices={levels}
                      direction="column"
                      name="activity"
                      legendText="Select your activity level"
                      onChange={value => {
                        field.onChange(value);
                        handleSubmit(submitForm)();
                      }}
                    />
                  </>
                )}
              />
            </FieldWrapper>
          </Question>
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithImage>
  );
};

ActivityForm.displayName = 'ActivityForm';

ActivityForm.propTypes = {
  activityQuestionPrompt: PropTypes.string.isRequired,
  defaultValues: PropTypes.shape({ activity: PropTypes.string }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.object.isRequired,
    }),
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  subheadline: PropTypes.string,
  ctaButtonText: PropTypes.object,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  flowType: PropTypes.string,
};
